import {
    AnchorButton,
    Divider,
    Position,
    Tab,
    Tabs,
    Tooltip
} from "@blueprintjs/core";
import { PROSPECTING_PATH } from "infrastructure/RouterApp";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { GeneratorType } from "types/generatorType";
import { RegionType } from "types/regionType";

import { getUrlForScreeningView, ScreeningView } from "types/screeningViewType";
import { REGION_TO_FUEL_TYPE_TO_URL_DICT } from "types/urlDirectoryType";
import "./RegionSelectorCard.scss";
import RegionSelectorTab from "./RegionSelectorTab";
import ScreeningViewSelectorTab from "./ScreeningViewSelectorTab";

type RegionAndGeneratorType = {
    readonly region: RegionType;
    readonly generatorType: GeneratorType;
};

type RegionSelectorCardProps = {
    readonly currentRegionAndGeneratorType?: RegionAndGeneratorType;
    readonly currentScreeningView?: ScreeningView;
    readonly onNavigate?: () => void;
};

const REGIONS_TAB_ID = "regions-tab";
const SCREENING_VIEWS_TAB_ID = "screening-views-tab";

const TAB_TYPES = [REGIONS_TAB_ID, SCREENING_VIEWS_TAB_ID] as const;
type TabType = (typeof TAB_TYPES)[number];

const RegionSelectorCard: React.FC<RegionSelectorCardProps> = (props) => {
    const { currentRegionAndGeneratorType, currentScreeningView, onNavigate } =
        props;
    const navigate = useNavigate();

    const [selectedTab, setSelectedTab] = useState<TabType>(
        getDefaultTab(currentRegionAndGeneratorType, currentScreeningView)
    );

    const [stagingRegionType, setStagingRegionType] = useState<
        RegionType | undefined
    >(currentRegionAndGeneratorType?.region);

    const [stagingGeneratorType, setStagingGeneratorType] = useState<
        GeneratorType | undefined
    >(currentRegionAndGeneratorType?.generatorType);

    const [stagingScreeningView, setStagingScreeningView] = useState<
        ScreeningView | undefined
    >(currentScreeningView);

    const canSubmit =
        (selectedTab === REGIONS_TAB_ID &&
            stagingGeneratorType !== undefined &&
            stagingRegionType !== undefined) ||
        (selectedTab === SCREENING_VIEWS_TAB_ID &&
            stagingScreeningView !== undefined);

    return (
        <>
            <div className="RegionSelectorCard-title">
                <Tabs
                    selectedTabId={selectedTab}
                    onChange={(tabId: TabType) => setSelectedTab(tabId)}
                >
                    <Tab
                        title={"Screening views"}
                        id={SCREENING_VIEWS_TAB_ID}
                    />
                    <Tab title={"Regions"} id={REGIONS_TAB_ID} />
                </Tabs>
            </div>
            <div className="RegionSelectorCard-body">
                {selectedTab === SCREENING_VIEWS_TAB_ID ? (
                    <ScreeningViewSelectorTab
                        screeningView={stagingScreeningView}
                        setScreeningView={setStagingScreeningView}
                    />
                ) : (
                    <RegionSelectorTab
                        stagingRegionType={stagingRegionType}
                        stagingGeneratorType={stagingGeneratorType}
                        setStagingRegionType={setStagingRegionType}
                        setStagingGeneratorType={setStagingGeneratorType}
                    />
                )}
            </div>
            <div className="RegionSelectorCard-footer">
                <Tooltip
                    content={
                        <div className="RegionSelectorCard-learn-more-wrapper bp5-dark">
                            <strong>
                                Select "Screening views" if you're a developer
                                doing early-stage screening for prospecting.
                            </strong>
                            <br />
                            <br />
                            Screening views are simplified views of Nira,
                            created by transmission engineers at your company.
                            Check with your transmission team on which views to
                            use.
                            <br />
                            <br />
                            <Divider />
                            <br />
                            <strong>
                                Select "Regions" if you're a transmission expert
                                and are doing detailed research on specific
                                POIs.
                            </strong>
                            <br />
                            <br />
                            These views contain the full functionality of Nira's
                            prospecting tools. You're able to view constraint
                            stacks, edit filters, tweak project sizes, etc.
                        </div>
                    }
                    position="top-left"
                >
                    <a>Learn more</a>
                </Tooltip>
                <Tooltip
                    content={
                        selectedTab === REGIONS_TAB_ID
                            ? "Select a region and fuel type."
                            : "Select a screening view."
                    }
                    position={Position.TOP_RIGHT}
                    disabled={canSubmit}
                >
                    <AnchorButton
                        intent="primary"
                        onClick={() => {
                            const maybeUrl = maybeGetUrl(
                                selectedTab,
                                stagingRegionType,
                                stagingGeneratorType,
                                stagingScreeningView
                            );
                            if (maybeUrl) {
                                navigate(maybeUrl.toLowerCase());
                                onNavigate?.();
                            }
                        }}
                        disabled={!canSubmit}
                    >
                        Confirm
                    </AnchorButton>
                </Tooltip>
            </div>
        </>
    );
};

const maybeGetUrl = (
    selectedTab: TabType,
    stagingRegionType: RegionType | undefined,
    stagingGeneratorType: GeneratorType | undefined,
    stagingScreeningView: ScreeningView | undefined
): string | undefined => {
    let maybeUrl: string | undefined;
    if (selectedTab === REGIONS_TAB_ID) {
        if (
            stagingRegionType !== undefined &&
            stagingGeneratorType !== undefined
        ) {
            const urlOrNull =
                REGION_TO_FUEL_TYPE_TO_URL_DICT[stagingRegionType][
                    stagingGeneratorType
                ];
            if (urlOrNull) {
                maybeUrl = `/${PROSPECTING_PATH}/${urlOrNull}`;
            }
        }
    } else if (selectedTab === SCREENING_VIEWS_TAB_ID) {
        if (stagingScreeningView !== undefined) {
            maybeUrl = getUrlForScreeningView(stagingScreeningView);
        }
    }

    return maybeUrl;
};

const getDefaultTab = (
    currentRegionAndGeneratorType: RegionAndGeneratorType | undefined,
    currentScreeningView: ScreeningView | undefined
): TabType => {
    if (
        currentRegionAndGeneratorType === undefined &&
        currentScreeningView === undefined
    ) {
        // If on home page, default to screening views. We could potentially change this depending on the users roles.
        return SCREENING_VIEWS_TAB_ID;
    } else if (currentScreeningView !== undefined) {
        return SCREENING_VIEWS_TAB_ID;
    } else {
        return REGIONS_TAB_ID;
    }
};

export default RegionSelectorCard;
